/**
 * Token Utility Functions
 * Handles JWT token validation and expiry checking
 */

/**
 * Decode JWT token without verification (client-side only)
 * Returns the payload or null if invalid
 */
export const decodeToken = (token) => {
  if (!token) return null;
  
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * Returns true if expired, false if valid
 */
export const isTokenExpired = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;
  
  // Token expiry is in seconds, Date.now() is in milliseconds
  const currentTime = Date.now() / 1000;
  return decoded.exp < currentTime;
};

/**
 * Get token from user object
 * Supports multiple token field names
 */
export const getToken = (user) => {
  if (!user) return null;
  return user.accessToken || user.token || user.jwt || null;
};

/**
 * The bearer token as it is actually persisted, read straight from
 * `localStorage["persist:user"]`.
 *
 * This is the same extraction `requestMethods.js` does inline for every HTTP
 * call; it is exported here because SOCKETS need it too. The server's socket
 * layer allows anonymous connections but gates room joins: `ticket:<id>` is in
 * `REQUIRES_AUTH`, so a socket that connects without a token is refused the
 * join and the customer silently receives no live ticket updates at all.
 */
export const getPersistedAuthToken = () => {
  try {
    const raw = window.localStorage.getItem("persist:user");
    if (!raw) return null;
    const persisted = JSON.parse(raw);
    const currentUser = persisted.currentUser && JSON.parse(persisted.currentUser);
    if (!currentUser) return null;
    return (
      currentUser.accessToken ||
      currentUser.token ||
      (currentUser.result && currentUser.result.token) ||
      (currentUser.result && currentUser.result.accessToken) ||
      null
    );
  } catch (error) {
    return null;
  }
};

/**
 * Check if user session is valid
 * Returns true if user has a valid token
 */
export const isSessionValid = (user) => {
  if (!user) return false;
  
  const token = getToken(user);
  if (!token) return false;
  
  return !isTokenExpired(token);
};

/**
 * Get time until token expires (in seconds)
 * Returns null if token is invalid or expired
 */
export const getTokenTimeRemaining = (token) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return null;
  
  const currentTime = Date.now() / 1000;
  const timeRemaining = decoded.exp - currentTime;
  
  return timeRemaining > 0 ? timeRemaining : 0;
};

/**
 * Format time remaining into human-readable format
 */
export const formatTimeRemaining = (seconds) => {
  if (!seconds || seconds <= 0) return 'Expired';
  
  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  return 'Less than a minute';
};
